import { SharingOptions } from '@gamiphy/bl/referrals/enums';
import SendByEmail from '@gamiphy/icons/SendByEmail';
import SquaredCopy from '@gamiphy/icons/SquaredCopy';
import WhatsApp from '@gamiphy/icons/WhatsApp';
import { LinkReferralDto, ReferralDto } from '@coretava/api/events-service';
import InviteFields from '@gamiphy/loyalty-station-desktop/components/circles/components/InviteFields';
import { ReferralsAnalytics } from '@gamiphy/loyalty-station-desktop/store/analytics';
import { CirclesStore } from '@gamiphy/loyalty-station-desktop/store/circle';
import { useTranslation } from '@gamiphy/react-i18n';
import { useOnCopy } from '@gamiphy/utils/react';
import {
  Box, Button, Card, Divider, Grid,
} from '@mui/material';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import InviteeList from './InviteeList';

export type Props = {
  onChangeState: () => void;
  appReferral: ReferralDto,
  linkReferral?: LinkReferralDto,
  whatsAppMessage: string
};

const InviteCard: React.FC<Props> = ({
  onChangeState,
  appReferral,
  linkReferral,
  whatsAppMessage,
}) => {
  const {
    copied,
    onCopy,
  } = useOnCopy(linkReferral?.link);
  const { t } = useTranslation();
  const { circles } = CirclesStore;
  const [showInviteeList, setShowInviteeList] = useState(false);
  const [showInviteFields, setShowInviteFields] = React.useState(false);
  const isThereConnection = circles.length > 0;

  const onCopyClick = React.useCallback(() => {
    void onCopy();
    ReferralsAnalytics.trackCopyClicked();
  }, []);

  const onWhatsAppClick = React.useCallback(() => {
    ReferralsAnalytics.trackWhatsappClicked();
  }, []);

  const isWhatsappEnabled = React.useMemo(() => appReferral?.enabledSharingOptions.includes(SharingOptions.whatsapp), [appReferral]);
  const isEmailEnabled = React.useMemo(() => appReferral?.enabledSharingOptions.includes(SharingOptions.email), [appReferral]);
  const isCopyLinkEnabled = React.useMemo(() => appReferral?.enabledSharingOptions.includes(SharingOptions.copyLink), [appReferral]);

  return (
    showInviteeList ? (<InviteeList handleClose={setShowInviteeList} />) : (
      <Card sx={{
        p: 0,
        position: 'relative',
        borderRadius: 'inherit',
        boxShadow: 'none',
      }}
      >
        <Grid
          container
          direction="column"
          alignItems="center"
        >
          {copied &&
            (
              <Box
                component="div"
                sx={{
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  position: 'absolute',
                  opacity: 0.7,
                  borderRadius: 20,
                  backgroundColor: ' #000000',
                  py: 1.5,
                  px: 2,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItem: 'center',
                  color: '#fff',
                  textAlign: 'center',
                  width: '60%',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%)',
                  whiteSpace: 'nowrap',
                  zIndex: 1,
                }}
              >
                {t('success.copy', { defaultValue: 'Copied to clipboard' })}
              </Box>
            )}
          {isThereConnection && (
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
              sx={{
                mt: '1.5rem',
              }}
            >
              <Button
                onClick={() => setShowInviteeList(true)}
                sx={{
                  p: 0,
                  fontSize: '1.2rem',
                  textAlign: 'right',
                  '& > span': {
                    fontSize: '1.2rem',
                  },
                }}
                variant="text"
                color="primary"
              >
                {t('inviteFriend.view.invitee', { defaultValue: 'View Your Invitees' })}
              </Button>
            </Grid>
          )}
          <Grid
            container
            justifyContent="center"
            sx={{
              fontSize: '1.2rem !important',
              fontWeight: 500,
              color: 'text.primary',
              textAlign: 'center',
              width: '100%',
              p: 2,
            }}
          >
            <Divider sx={{
              width: '50%',
              borderBottomWidth: '4px',
              borderRadius: '2px',
            }}
            />
          </Grid>
          <Grid sx={{
            padding: '1rem 1.6rem',
            width: '100%',
          }}
          >
            <Grid container direction="row" alignItems="flex-end" justifyContent="space-around" wrap="nowrap">
              {isEmailEnabled && (
                <Grid
                  container
                  direction="column"
                  alignItems="center"
                  sx={{
                    cursor: 'pointer',
                  }}
                  onClick={() => setShowInviteFields(true)}
                >
                  <Grid>
                    <SendByEmail />
                  </Grid>
                  <Grid>
                    {t('shared.email', { defaultValue: 'Email' })}
                  </Grid>
                </Grid>
              )}
              {isWhatsappEnabled && (
                <Grid
                  container
                  direction="column"
                  alignItems="center"
                  sx={{
                    width: 'fit-content',
                    position: 'relative',
                    overflow: 'visible',
                    cursor: 'pointer',
                  }}
                >
                  <Grid>
                    <a
                      href={`https://api.whatsapp.com/send?text=${encodeURI(whatsAppMessage)}`}
                      rel="noreferrer"
                      target="_blank"
                      aria-label="whatsapp"
                      onClick={onWhatsAppClick}
                    >
                      <WhatsApp />
                    </a>
                  </Grid>
                  <Grid sx={{
                    color: '#181b32',
                    fontSize: '1.2rem',
                  }}
                  >
                    {t('whatsapp', { defaultValue: 'Whatsapp' })}
                  </Grid>
                </Grid>
              )}
              {isCopyLinkEnabled && (
                <Grid
                  sx={{
                    width: 'fit-content',
                    position: 'relative',
                    overflow: 'visible',
                    cursor: 'pointer',
                  }}
                  container
                  direction="column"
                  alignItems="center"
                  onClick={onCopyClick}
                >
                  <Grid sx={{ cursor: 'pointer' }}>
                    <SquaredCopy width="3.8rem" height="3.8rem" />
                  </Grid>
                  <Grid sx={{
                    color: '#181b32',
                    fontSize: '1.2rem',
                  }}
                  >
                    {t('shared.copyLink', { defaultValue: 'Copy link' })}
                  </Grid>
                </Grid>
              )}
            </Grid>
            {showInviteFields && (
              <Grid>
                <InviteFields onChangeState={onChangeState} appReferral={appReferral} />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Card>
    )
  );
};

export default observer(InviteCard);
